import React, { useEffect, useState, useMemo } from "react";
import "../style.css";
import { CartProvider } from "./providers/CartProvider";
import BackgroundImage from "../../../components/ui/BackgroundImage";
import bgImg from "../assets/images/bg.webp";
import specsImg from "../assets/images/specs.png";
import CategoryList from "./categories/CategoryList";
import ProductList from "./items/ProductList";
import SpecificationList from "./specifications/SpecificationList";
import ColorPicker from "./color-picker/ColorPicker";
import CartOverview from "./cart/CartOverview";
import { motion, AnimatePresence } from "framer-motion";
import { Category, Product } from "../types";
import categoriesJSON from "../data/categories.json";
import productsJSON from "../data/products.json";
import specificationsJSON from "../data/specifications.json";

interface Props {
  className?: string;
}

// 1) High level animation variants centrally defined for reuse
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.12,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 14, scale: 0.995 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", damping: 16, stiffness: 120 },
  },
  exit: { opacity: 0, y: 10, transition: { duration: 0.18 } },
};

const spotlight = {
  idle: { scale: 1 },
  hover: {
    scale: 1.03,
    transition: { type: "spring", stiffness: 300, damping: 20 },
  },
  tap: { scale: 0.98 },
};

export default function TuningWithMotion({ className }: Props) {
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    categoriesJSON[0]
  );
  const [products, setProducts] = useState<Product[]>(
    productsJSON as Product[]
  );

  useEffect(() => {
    if (!selectedCategory) {
      setProducts(productsJSON as Product[]);
      return;
    }

    setProducts(
      (productsJSON as Product[]).filter(
        (p) => p.categoryId === selectedCategory?.id
      )
    );
  }, [selectedCategory?.id]);

  // derived memo to keep layout animations stable when filtering
  const gridKey = useMemo(
    () => (selectedCategory ? `cat-${selectedCategory.id}` : "all"),
    [selectedCategory]
  );

  return (
    <CartProvider>
      <BackgroundImage
        imageSrc={bgImg}
        imageClassName="!object-fill"
        className={`h-screen overflow-hidden w-full relative gilroy-font ${className}`}
      >
        {/* floating specs image with a slow float animation */}
        <motion.img
          src={specsImg}
          alt="specs"
          draggable={false}
          initial={{ opacity: 0, y: -20, scale: 0.98 }}
          animate={{ opacity: 1, y: [-6, 6, -6], scale: 1 }}
          transition={{
            duration: 2,

            repeatType: "reverse",
            ease: "easeInOut",
          }}
          className="absolute top-0 z- left-0 z-[-1] select-none pointer-events-none max-w-[370px] 1366px:max-w-[430px] 1520px:max-w-full"
        />

        <main className="h-full min-h-0 w-full grid justify-between  gap-2 grid-cols-[24%,51%,24%]  1920px:grid-cols-[20%,58%,20%] pt-[3rem] 1366px:pt-[3.5rem] 1520px:pt-[4.5rem] pb-10 items-end">
          {/* Categories column - animate entrance and highlight */}
          <motion.aside
            className="h-full min-h-0"
            variants={containerVariants}
            initial="hidden"
            animate="show"
            exit="hidden"
            layout
          >
            <motion.div variants={fadeUp as any} className="h-full min-h-0">
              <CategoryList
                className="h-full min-h-0"
                selectedCategoryId={selectedCategory?.id || null}
                categories={categoriesJSON}
                includeHeading
                onCategoryClick={(category) => setSelectedCategory(category)}
              />
            </motion.div>
          </motion.aside>

          {/* Items (center) - staggering, shared-layout and graceful filter transitions */}
          <section className="relative">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="show"
              className="h-full min-h-0"
            >
              <AnimatePresence mode="popLayout">
                {/* key the whole list by gridKey so that switching categories triggers a smooth layout animation */}
                <motion.div
                  key={gridKey}
                  layout
                  variants={fadeUp as any}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                  className="h-full min-h-0"
                >
                  {/* ProductList should accept motion props — if not, it's wrapped here */}

                  <ProductList
                    products={products}
                    selectedCategory={selectedCategory}
                  />
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* subtle page-level accent: entrance glow */}
            <motion.div
              aria-hidden
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.7 }}
              className="pointer-events-none absolute inset-0 mix-blend-screen"
            />
          </section>

          {/* Right column: specs, color picker, cart */}
          <motion.aside
            className="h-full min-h-0 flex flex-col justify-between"
            layout
          >
            <motion.div
              variants={fadeUp as any}
              className="mb-3 h-full min-h-0"
            >
              <SpecificationList
                specifications={specificationsJSON}
                includeHeading
              />
            </motion.div>

            <motion.div
              variants={fadeUp as any}
              className="flex-shrink-0"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 }}
            >
              <ColorPicker
                showCopyColor
                className="flex-shrink-0"
                includeHeading
                barHeight={100}
              />
            </motion.div>

            <motion.div
              className="pr-14 mt-5"
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 160, damping: 20 }}
            >
              <CartOverview />
            </motion.div>
          </motion.aside>
        </main>
      </BackgroundImage>
    </CartProvider>
  );
}
